import GetAppIcon from '@mui/icons-material/GetApp';
import { Alert, Backdrop, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, Divider, FormControl, Grid, InputLabel, MenuItem, Select, styled, TextField } from "@mui/material";
import { BusinessPartnerInfo, FlightAndOrderLog, getSizeLabel, isBusinessDestinationInfo, isCustomerDestinationInfo, isMaritimeDestinationInfo, isMaritimeRequesterInfo, OperationPartnerInfo, PartnerKind, RequesterInfoType, sumTotalWeight, UserInfo } from 'adoms-common-lib';
import axios from 'axios';
import * as Excel from 'exceljs';
import React, { useEffect, useState } from 'react';
import { useUserInfoContext } from '../../common/UserContext';
import { APIConnector } from "../../connector/APIConnector";

const StyledTextField = styled(TextField)(({ theme }) => ({
    width: 200,
}));

const StyledButton = styled(Button)(({ theme }) => ({
    marginTop: theme.spacing(1)
}));

const StyledBackdrop = styled(Backdrop)(({ theme }) => ({
    marginTop: theme.spacing(1)
}));

type OrderLogOutputDialogProps = {
    onClose: () => void
    isOpen: boolean
};

export const OrderLogOutputDialog: React.FC<OrderLogOutputDialogProps> = (props) => {
    const userInfoContext = useUserInfoContext();

    const [scroll, setScroll] = React.useState<DialogProps['scroll']>('body');
    const [orderDateFrom, setOrderDatedFrom] = React.useState("");
    const [orderDateTo, setOrderDatedTo] = React.useState("");
    const [isDisplayLoadingMark, setDisplayLoadingMark] = useState<boolean>(false);
    const [orderDateFromErrorMessage, setOrderDateFromErrorMessage] = React.useState<string>();
    const [orderDateToErrorMessage, setOrderDateToErrorMessage] = React.useState<string>();
    const [infoMessage, setInfoMessage] = React.useState<string>();
    const [errorMessage, setErrorMessage] = React.useState<string>();
    const [userErrorMessage, setUserErrorMessage] = useState<string | undefined>(undefined);
    const [userInfo, setUserInfo] = useState<UserInfo | undefined>(userInfoContext.userInfo);
    const [businessPartnerInfoList, setBusinessPartnerInfoList] = React.useState<BusinessPartnerInfo[]>([]);
    const [selectedBusinessPartnerId, setSelectedBusinessPartnerId] = React.useState<string>();


    // ユーザー情報を取得する
    useEffect(() => {
        setUserInfo(userInfoContext.userInfo);
        if (userInfoContext.isError) {
            setUserErrorMessage("ユーザー情報を取得できませんでした。");
        } else {
            setUserErrorMessage(undefined);
        }
    }, [userInfoContext]);

    // ビジネスパートナーリストを取得
    useEffect(() => {
        const fetchInitialData = async () => {
            const visibleBusinessPartnerInfoList =
                getVisibleBusinessPartnerInfoList(userInfo);
            setBusinessPartnerInfoList(visibleBusinessPartnerInfoList);
        };
        fetchInitialData();
    }, [userInfo]);

    // ユーザー情報からビスネスパートナーリストの取得
    const getVisibleBusinessPartnerInfoList = (userInfo?: UserInfo): BusinessPartnerInfo[] => {
        let visibleBusinessPartnerInfoList = new Array<BusinessPartnerInfo>();
        const userPartnerInfo = userInfo?.userPartnerInfo;

        if (userPartnerInfo?.partnerKind === PartnerKind.Business) {
            visibleBusinessPartnerInfoList.push(
                userPartnerInfo.partnerInfo as BusinessPartnerInfo
            );
        } else if (userPartnerInfo?.partnerKind === PartnerKind.Operation) {
            for (let visibleBusinessPartnerInfo of (
                userPartnerInfo.partnerInfo as OperationPartnerInfo
            ).visibleBusinessPartnerInfoList) {
                visibleBusinessPartnerInfoList.push(visibleBusinessPartnerInfo);
            }
        }
        return visibleBusinessPartnerInfoList;
    };

    // 配送ステータスの英語と日本語の対応表
    const receiveStatusMap: { [key: string]: string } = {
        "Received": "配送済み",
        "Loaded": "荷物搭載済み",
        "Preparation": "搭載準備中",
        "NotReceived": "未受領",
        "WaitingForPickup": "集荷待ち",
        "Cancel": "キャンセル"
    };

    /**
     * ダイアログをクローズする
     */
    const handleFlightLogOutputDialogClose = () => {
        setOrderDatedFrom("")
        setOrderDatedTo("")
        setOrderDateFromErrorMessage("")
        setOrderDateToErrorMessage("")
        setInfoMessage("")
        setErrorMessage("")
        setUserErrorMessage(undefined)
        setSelectedBusinessPartnerId(undefined)
        props.onClose();
    };

    /**
     * 対象期間開始日が変わった時のハンドラ
     * @param event 
     */
    const handleChangeOrderDateFrom = (event: any) => {
        const value = event.target.value;
        setOrderDatedFrom(value);
        // 値が入力されたらエラーメッセージをクリアする
        if (value) {
            setOrderDateFromErrorMessage("");
        } else {
            setOrderDateFromErrorMessage("開始日を入力してください");
        };
    };

    /**
     * 対象期間終了日が変わった時のハンドラ
     * @param event 
     */
    const handleChangeOrderDateTo = (event: any) => {
        const value = event.target.value;
        setOrderDatedTo(value);
        // 値が入力されたらエラーメッセージをクリアする
        if (value) {
            setOrderDateToErrorMessage("");
        } else {
            setOrderDateToErrorMessage("終了日を入力してください");
        }
    };

    /**
     * Excel出力ボタンが押された時のハンドラ
     * @param event
     */
    const clickExcelOutputButtonHandler = async (event: any) => {
        setDisplayLoadingMark(true)
        setOrderDatedFrom("");
        setOrderDatedTo("");
        setOrderDateFromErrorMessage("");
        setOrderDateToErrorMessage("");

        let existError = false;
        if (!orderDateFrom) {
            setOrderDateFromErrorMessage("開始日を入力してください");
            existError = true;
        };
        if (!orderDateTo) {
            setOrderDateToErrorMessage("終了日を入力してください");
            existError = true;
        };
        if (new Date(orderDateFrom) > new Date(orderDateTo)) {
            setOrderDateToErrorMessage("終了日は開始日以降の日付を入力してください");
            existError = true;
        };
        if (existError) {
            setDisplayLoadingMark(false);
            return;
        };

        // テンプレートファイルの呼び出し
        // TODO: 未確認
        const res = await axios.get("/orderLogTemplate.xlsx", { responseType: "arraybuffer" });
        const workbook = new Excel.Workbook();
        await workbook.xlsx.load(res.data);

        let apigw: APIConnector = APIConnector.instance;

        // 出力する飛行実績を取得
        await apigw.getOrderLog(orderDateFrom, orderDateTo, selectedBusinessPartnerId).then(async (orderLogList: FlightAndOrderLog[]) => {
            if (orderLogList.length === 0) {
                setDisplayLoadingMark(false);
                setInfoMessage("条件に該当する配送実績はありません");
                return
            };

            const orderLogWorksheet = workbook.getWorksheet('実績');
            if (orderLogWorksheet == null) {
                setDisplayLoadingMark(false);
                setErrorMessage("テンプレートファイルの読み込みに失敗しました");
                return;
            }

            orderLogList.forEach((orderLogData, index: number) => {
                // 配送実績の表が開始する行
                const orderLogStartRowNumber = 3;

                let orderLogRow = orderLogWorksheet.getRow(orderLogStartRowNumber + index);
                if (index > 9) {
                    // 10行目以降は行を追加する
                    orderLogWorksheet.insertRow(orderLogStartRowNumber + index, "", "i+");
                };

                let flightLog = orderLogData.flightLog;
                let orderLog = orderLogData.orderLog;

                //TODO: 実績関連のデータ処理は暫定対応
                if (orderLog != null) {
                    const requesterNameText = (() => {
                        const requesterAny = orderLog.requester as any;
                        if (requesterAny?.type === RequesterInfoType.MaritimeRequesterInfo) {
                            const hq = requesterAny?.supplyHeadquartersInfo;
                            return `${hq?.lastname ?? ""}${hq?.firstname ?? ""}`.trim();
                        }
                        if (
                            requesterAny?.type === RequesterInfoType.BusinessRequesterInfo
                            || requesterAny?.type === RequesterInfoType.CustomerRequesterInfo
                        ) {
                            return `${requesterAny?.lastname ?? ""}${requesterAny?.firstname ?? ""}`.trim();
                        }
                        return `${requesterAny?.lastname ?? ""}${requesterAny?.firstname ?? ""}`.trim();
                    })();

                    const destinationAddressText = (() => {
                        const destination = orderLog.destination;
                        if (isMaritimeDestinationInfo(destination)) {
                            return destination.transportRequestUnit.unitName ?? "";
                        }
                        if (isBusinessDestinationInfo(destination) || isCustomerDestinationInfo(destination)) {
                            return `${destination.prefecture ?? ""}${destination.city ?? ""}${destination.address ?? ""}`;
                        }
                        return "";
                    })();

                    const destinationNameText = (() => {
                        const destination = orderLog.destination;
                        if (isMaritimeDestinationInfo(destination)) {
                            const unit = destination.transportRequestUnit;
                            const name = `${unit?.lastname ?? ""}${unit?.firstname ?? ""}`.trim();
                            return name || (unit?.unitName ?? "");
                        }

                        if (isBusinessDestinationInfo(destination) || isCustomerDestinationInfo(destination)) {
                            return `${destination.lastname ?? ""}${destination.firstname ?? ""}`.trim();
                        }
                        return "";
                    })();

                    const firstGoods = orderLog.goods?.[0];

                    const goodsNameText = orderLog.goods?.map((good, index) => {
                        return (index + 1) + "：" + good.name +
                            "　" + getSizeLabel(good) +
                            "　" + (good.weight ?? "ー") + "g";
                    }).join("\r\n") ?? "";

                    const goodsTotalWeightText = sumTotalWeight(orderLog.goods);

                    orderLogRow.getCell(1).value = orderLog.orderID;
                    orderLogRow.getCell(2).value = orderLog.externalOrderID;
                    orderLogRow.getCell(3).value = orderLog.preferredDeliveryDate;
                    orderLogRow.getCell(4).value = orderLog.preferredDeliveryTime;
                    orderLogRow.getCell(5).value = requesterNameText;
                    orderLogRow.getCell(6).value = orderLog.businessPartnerLabel;
                    orderLogRow.getCell(7).value = destinationAddressText;
                    orderLogRow.getCell(8).value = destinationNameText;
                    orderLogRow.getCell(9).value = receiveStatusMap[orderLog.receiveStatus];
                    if (isMaritimeRequesterInfo(orderLog.requester) && isMaritimeDestinationInfo(orderLog.destination)) {
                        orderLogRow.getCell(10).value = goodsNameText;
                        orderLogRow.getCell(11).value = goodsTotalWeightText > 0 ? `${goodsTotalWeightText}` : "ー";
                    } else {
                        orderLogRow.getCell(10).value = orderLog.goods?.map((good, index) => {
                            return (index + 1) + "：" + good.name;
                        }).join("\r\n") ?? "";
                        orderLogRow.getCell(11).value = firstGoods?.weight ?? "";
                    };
                    orderLogRow.getCell(12).value = orderLog.goods?.length ?? "";
                    orderLogRow.getCell(13).value = orderLog.remarks;
                };
                orderLogRow.getCell(14).value = flightLog.flightNum;
                orderLogRow.getCell(15).value = flightLog.yearMonthDate;
                orderLogRow.getCell(16).value = flightLog.fdName;
                orderLogRow.getCell(17).value = flightLog.competenceCertificateNumber;
                orderLogRow.getCell(18).value = flightLog.purpose;
                orderLogRow.getCell(19).value = flightLog.specificList?.join("、");
                orderLogRow.getCell(20).value = flightLog.flightPath.departure.portName + '\r\n'
                    + flightLog.flightPath.departure.latitude + ","
                    + flightLog.flightPath.departure.longitude;;
                orderLogRow.getCell(21).value = flightLog.flightPath.arrival.portName + "\r\n"
                    + flightLog.flightPath.arrival.latitude + ","
                    + flightLog.flightPath.arrival.longitude;;
                orderLogRow.getCell(22).value = flightLog.std;
                orderLogRow.getCell(23).value = flightLog.sta;
                orderLogRow.getCell(24).value = flightLog.etd;
                orderLogRow.getCell(25).value = flightLog.eta;
                orderLogRow.getCell(26).value = flightLog.atd;
                orderLogRow.getCell(27).value = flightLog.ata;
                orderLogRow.getCell(28).value = flightLog.flightTime;
                orderLogRow.getCell(29).value = flightLog.totalFlightTime;
                orderLogRow.getCell(30).value = flightLog.affectedFlightSafetyMatters;
            });

            // UInt8Arrayを生成
            const uint8Array = await workbook.xlsx.writeBuffer();
            // Blobを生成
            const blob = new Blob([uint8Array], { type: 'application/octet-binary' });
            // DL用URLを生成し、aタグからダウンロードを実行
            const url = window.URL.createObjectURL(blob);
            // aタグを生成
            const a = document.createElement('a');
            // aタグのURLを設定
            a.href = url;
            // aタグにdownload属性を付け、URLがダウンロード対象になるようにする
            a.download = "配送実績_" + orderDateFrom
                + "～" + orderDateTo + ".xlsx";
            // aタグをクリック
            a.click();
            // ダウンロード後は不要なのでaタグを除去
            a.remove();
            setDisplayLoadingMark(false);
            // ダイアログを閉じる
            handleFlightLogOutputDialogClose();
        }).catch((error) => {
            setDisplayLoadingMark(false);
            console.log(error.response);
            if (axios.isAxiosError(error)
                && typeof error.response !== "undefined"
                && error.response.status === 403) {
                setErrorMessage("権限がありません");
            } else {
                setErrorMessage("システムエラーが発生しました");
            };
        });
    };

    return (
        <Dialog
            open={props.isOpen}
            onClose={handleFlightLogOutputDialogClose}
            scroll={scroll}
            aria-labelledby="scroll-dialog-title"
            aria-describedby="scroll-dialog-description"
        >
            <DialogTitle id="scroll-dialog-title">配送実績出力<Divider /></DialogTitle>
            <DialogContent dividers={scroll === 'paper'}>
                <Grid container direction="column" spacing={2}>
                    {infoMessage ?
                        <Grid item>
                            <Alert
                                severity="info">{infoMessage}</Alert>
                        </Grid>
                        : undefined
                    }
                    {userErrorMessage ?
                        <Grid item>
                            <Alert
                                severity="error">{userErrorMessage}</Alert>
                        </Grid>
                        : undefined
                    }
                    {errorMessage ?
                        <Grid item>
                            <Alert
                                severity="error">{errorMessage}</Alert>
                        </Grid>
                        : undefined
                    }
                    <Grid item container direction="row" spacing={2} sx={{ mt: "0px" }}>
                        <Grid item>
                            <StyledTextField
                                required
                                id="TargetPeriodFrom"
                                label="開始日"
                                type="date"
                                value={orderDateFrom}
                                variant="outlined"
                                onChange={handleChangeOrderDateFrom}
                                error={orderDateFromErrorMessage ? true : false}
                                helperText={orderDateFromErrorMessage ? orderDateFromErrorMessage : ""}
                                InputLabelProps={{
                                    shrink: true,
                                }} />
                        </Grid>
                        <Grid item>
                            <StyledTextField
                                required
                                id="TargetPeriodEnd"
                                label="終了日"
                                type="date"
                                value={orderDateTo}
                                variant="outlined"
                                onChange={handleChangeOrderDateTo}
                                error={orderDateToErrorMessage ? true : false}
                                helperText={orderDateToErrorMessage ? orderDateToErrorMessage : ""}
                                InputLabelProps={{
                                    shrink: true,
                                }} />
                        </Grid>
                    </Grid>
                    <Grid item style={{ paddingTop: "16px" }}>
                        <FormControl style={{ width: 200 }}>
                            <InputLabel id="business-partner-select-label">ビジネスパートナー</InputLabel>
                            <Select
                                style={{ width: 200 }}
                                value={selectedBusinessPartnerId}
                                label="ビジネスパートナー"
                                onChange={(e) => {
                                    setSelectedBusinessPartnerId(e.target.value);
                                }}>
                                <MenuItem value={""}>
                                    選択しない
                                </MenuItem>
                                {businessPartnerInfoList.map((businessPartnerInfo, index: number) => (
                                    <MenuItem key={index} value={businessPartnerInfo.businessPartnerId}>
                                        {businessPartnerInfo.businessPartnerName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <DialogActions style={{ paddingRight: "0%" }}>
                    <StyledButton
                        variant="contained"
                        color="primary"
                        type="submit"
                        startIcon={<GetAppIcon />}
                        onClick={(e) => clickExcelOutputButtonHandler(e)}
                        disabled={!orderDateFrom || !orderDateTo}
                    >
                        Excel出力
                    </StyledButton>
                </DialogActions>
            </DialogContent>
            <StyledBackdrop open={isDisplayLoadingMark}>
                <CircularProgress color="inherit" />
            </StyledBackdrop>
        </Dialog>
    );
};